import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { useSearchParams } from "react-router-dom"
import { UsersIcon, ChevronDownIcon } from "lucide-react"
import IssuePage from "~/components/issuePage/IssuePage"
import { ErrorState, LoadingState } from "~/components/screens/StateScreen"
import { Button } from "~/components/ui/button"
import { SearchableSelect } from "~/components/ui/searchable-select"
import { useOrganization } from "~/hooks/useOrganization"
import { api } from "~/trpc/react"

export default function MyIssuesPage() {
	const { organization } = useOrganization()
	const [params, setParams] = useSearchParams()
	const selectedTeams = params.getAll("team")
	const {
		data: userIssues,
		isLoading,
		error
	} = api.issues.getUserIssues.useQuery(
		{ organizationId: organization?.id ?? "" },
		{ enabled: !!organization }
	)
	const teams = api.user.getTeams.useQuery(
		{ organizationId: organization?.id ?? "" },
		{ enabled: !!organization }
	)
	if (isLoading || !organization)
		return <LoadingState className="bg-transparent" />
	if (error || !userIssues)
		return (
			<ErrorState
				title="Unable to load issues."
				description={error?.message ?? "Your issues are unavailable."}
				className="bg-transparent"
			/>
		)
	const issues = userIssues.issues.filter(
		(issue) => !selectedTeams.length || selectedTeams.includes(issue.teamId)
	)
	return (
		<IssuePage
			key={organization.id}
			teamIssueData={{
				issues,
				statuses: [...new Set(issues.map((issue) => issue.status))]
			}}
			customNoIssuesMessage={
				selectedTeams.length
					? "No issues assigned to you in the selected teams. Try another team or clear the team filter."
					: "No issues assigned to you in this organisation."
			}
			filters={
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="flex flex-wrap items-center gap-2">
						<SearchableSelect
							multiple
							value={selectedTeams}
							options={(teams.data ?? []).map((team) => ({
								value: team.id,
								label: team.name
							}))}
							onChange={(value) => {
								if (posthogEnabled)
									posthog.capture("my_issues_teams_filtered", {
										team_count: value.length
									})
								setParams(
									(previous) => {
										const next = new URLSearchParams(previous)
										next.delete("team")
										for (const id of value) next.append("team", id)
										return next
									},
									{ replace: true }
								)
							}}
						>
							<Button variant="outline" size="sm">
								<UsersIcon className="size-4" />
								{selectedTeams.length === 1
									? (teams.data?.find((team) => team.id === selectedTeams[0])
											?.name ?? "Selected team")
									: selectedTeams.length
										? `${selectedTeams.length} teams`
										: "All teams"}
								<ChevronDownIcon className="size-3" />
							</Button>
						</SearchableSelect>
						{selectedTeams.length > 0 && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									setParams(
										(previous) => {
											const next = new URLSearchParams(previous)
											next.delete("team")
											return next
										},
										{ replace: true }
									)
								}
							>
								Clear teams
							</Button>
						)}
					</div>
					<span className="text-xs text-text-small">
						Assigned to me · {organization.name}
					</span>
				</div>
			}
		/>
	)
}
